// Every chain the baseline service serves today is EVM, and the health payload
// carries no format rules — so screening validates against the EVM shapes.
export const EVM_ADDRESS_REGEX = '^0x[a-fA-F0-9]{40}$'
export const EVM_TX_REGEX = '^0x[a-fA-F0-9]{64}$'

// The health endpoint returns slugs; these are the ones whose display name is not
// just a capitalised slug. Anything missing falls back to title-casing.
const CHAIN_NAMES = {
  'polygon-zkevm': 'Polygon zkEVM',
  'immutable-zkevm': 'Immutable zkEVM',
  'manta-pacific': 'Manta Pacific',
  zksync: 'zkSync',
  opbnb: 'opBNB',
  hyperevm: 'HyperEVM',
  pulsechain: 'PulseChain',
  worldchain: 'World Chain',
  apechain: 'ApeChain',
  etherlink: 'Etherlink',
  swellchain: 'Swellchain',
}

export const chainDisplayName = key =>
  CHAIN_NAMES[key] ||
  String(key || '')
    .split('-')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

// One health entry in the shape the selector, the validation and the report
// request read. Older payloads carried only the declaration, hence the
// fallback.
const capabilityReady = (status, declared) =>
  status === undefined ? Boolean(declared) : status === 'ready'

const servesAnyCheck = chain =>
  capabilityReady(chain.addressStatus, chain.address && chain.status === 'ready') ||
  capabilityReady(chain.transactionStatus, chain.transaction && chain.status === 'ready')

export const normalizeBaselineChain = chain => ({
  key: chain.key,
  blockchain: chainDisplayName(chain.key),
  label: chain.symbol,
  symbol: chain.symbol,
  coin: chain.symbol,
  family: 'eth',
  addressRegex: EVM_ADDRESS_REGEX,
  txRegex: EVM_TX_REGEX,
  // What it can serve now, not what it advertises.
  supportsAddress: capabilityReady(chain.addressStatus, chain.address),
  supportsTransaction: capabilityReady(chain.transactionStatus, chain.transaction),
  stableCoins: Array.isArray(chain.stableCoins) ? chain.stableCoins : [],
  // The native asset plus every stablecoin the chain reports.
  tokens: [
    { symbol: chain.symbol, address: '', decimals: 18, native: true },
    ...(Array.isArray(chain.stableCoins) ? chain.stableCoins : []),
  ],
})

// A chain is offered when it can serve at least one kind of check. The
// top-level `status` is a roll-up - 'degraded' when only the transaction side is
// down - and filtering on it alone hid four chains whose address checks worked
// (GLBA-13491).
export const readyBaselineChains = (chains = []) =>
  chains
    .filter(chain => chain?.key && servesAnyCheck(chain))
    .map(normalizeBaselineChain)
    .sort((a, b) => a.blockchain.localeCompare(b.blockchain))

// Every chain the service knows, health notwithstanding. Screening must not
// offer a chain it cannot check, but a history filter names chains that already
// wrote rows - filtering by health would hide those rows for the outage.
export const allBaselineChains = (chains = []) =>
  chains
    .filter(chain => chain?.key)
    .map(normalizeBaselineChain)
    .sort((a, b) => a.blockchain.localeCompare(b.blockchain))

// Identity of the offered set, so polling can skip no-op commits. Availability
// belongs in it: without that, a chain coming back up never reaches the
// dropdown.
export const chainsFingerprint = (chains = []) =>
  chains
    .map(chain =>
      [
        chain.key,
        chain.symbol,
        chain.supportsAddress,
        chain.supportsTransaction,
        (chain.tokens || []).map(t => t.address).join(','),
      ].join(':'),
    )
    .join('|')
